/**
 * Poem Cache Service
 * Handles caching of poems for the Persian loader with browser session persistence
 */

import { apiService, RequestConfig } from './api';

export interface CachedPoem {
    id: number;
    title: string;
    content: string;
    author: string;
    category: string;
    isActive: boolean;
    translation?: string;
    description?: string;
    displayOrder?: number;
    createdAt: string;
    updatedAt?: string;
    createdBy?: string;
    updatedBy?: string;
}

export interface PoemCache {
    poems: CachedPoem[];
    timestamp: number;
    version: string;
}

export interface PaginatedPoemResponse {
    poems: CachedPoem[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

class PoemCacheService {
    private static instance: PoemCacheService;
    private cache: CachedPoem[] | null = null;
    private cacheKey = 'persian_poems_cache';
    private cacheVersion = '1.0';
    private isLoading = false;
    private loadPromise: Promise<CachedPoem[]> | null = null;

    private constructor() {
        this.loadFromSessionStorage();
    }

    public static getInstance(): PoemCacheService {
        if (!PoemCacheService.instance) {
            PoemCacheService.instance = new PoemCacheService();
        }
        return PoemCacheService.instance;
    }

    /**
     * Load poems from cache or API
     */
    public async getPoems(): Promise<CachedPoem[]> {
        // If we have cached poems, return them immediately
        if (this.cache && this.cache.length > 0) {
            console.log('PoemCache: Returning cached poems:', this.cache.length);
            return this.cache;
        }

        // If already loading, return the existing promise
        if (this.isLoading && this.loadPromise) {
            console.log('PoemCache: Already loading, waiting for existing request...');
            return this.loadPromise;
        }

        // Start loading poems
        this.isLoading = true;
        this.loadPromise = this.loadPoemsFromAPI();

        try {
            const poems = await this.loadPromise;
            this.cache = poems;
            this.saveToSessionStorage();
            console.log('PoemCache: Successfully loaded and cached poems:', poems.length);
            return poems;
        } finally {
            this.isLoading = false;
            this.loadPromise = null;
        }
    }

    /**
     * Get a random poem from cache
     */
    public async getRandomPoem(): Promise<CachedPoem | null> {
        try {
            const poems = await this.getPoems();
            const activePoems = poems.filter(poem => poem.isActive);

            if (activePoems.length === 0) {
                console.log('PoemCache: No active poems available');
                return null;
            }

            const randomIndex = Math.floor(Math.random() * activePoems.length);
            const selectedPoem = activePoems[randomIndex];
            if (selectedPoem) {
                console.log('PoemCache: Selected random poem from database:', selectedPoem.title);
                return selectedPoem;
            }
            return null;
        } catch (error) {
            console.error('PoemCache: Failed to get random poem:', error);
            return null;
        }
    }

    /**
     * Force refresh cache from API
     */
    public async refreshCache(): Promise<CachedPoem[]> {
        console.log('PoemCache: Force refreshing cache...');
        this.cache = null;
        this.clearSessionStorage();
        return this.getPoems();
    }

    /**
     * Check if cache is available
     */
    public hasCachedPoems(): boolean {
        return this.cache !== null && this.cache.length > 0;
    }

    /**
     * Get cache statistics
     */
    public getCacheStats(): { totalPoems: number; activePoems: number; cached: boolean } {
        if (!this.cache) {
            return { totalPoems: 0, activePoems: 0, cached: false };
        }

        const activePoems = this.cache.filter(poem => poem.isActive).length;
        return {
            totalPoems: this.cache.length,
            activePoems,
            cached: true
        };
    }

    /**
     * Load poems from the new public API endpoint
     */
    private async loadPoemsFromAPI(): Promise<CachedPoem[]> {
        try {
            console.log('PoemCache: Loading poems from public API...');

            // Keep poem fetch fast so the loader doesn't hang on long retries/timeouts
            const poemRequestConfig: RequestConfig = {
                timeout: 5000,
                retries: 0,
                cache: true,
            };

            // Use the new public endpoint from swagger - expecting paginated response
            const response = await apiService.get<PaginatedPoemResponse>(
                '/api/Poem?pageNumber=1&pageSize=100&isActive=true',
                poemRequestConfig
            );

            console.log('PoemCache: Raw API response:', response);
            console.log('PoemCache: Response type:', typeof response);

            // Check if response has the expected paginated structure
            if (response && typeof response === 'object' && 'poems' in response && Array.isArray(response.poems)) {
                const poems = response.poems;
                console.log('PoemCache: Successfully loaded poems from paginated API response:', poems.length);
                console.log('PoemCache: Pagination info:', {
                    totalCount: response.totalCount,
                    pageNumber: response.pageNumber,
                    pageSize: response.pageSize,
                    totalPages: response.totalPages
                });

                if (poems.length > 0 && poems[0]) {
                    console.log('PoemCache: First poem sample:', poems[0]);
                }

                // Since the API endpoint already filters by isActive=true, 
                // we can assume all returned poems are active
                // But let's ensure the isActive field is set correctly
                const poemsWithActiveFlag = poems.map(poem => ({
                    ...poem,
                    isActive: poem.isActive !== undefined ? poem.isActive : true
                }));

                console.log('PoemCache: Processed poems with isActive flag:', poemsWithActiveFlag.length);
                return poemsWithActiveFlag;
            } else if (Array.isArray(response)) {
                // Fallback: handle direct array response (in case API changes)
                console.log('PoemCache: API returned direct array (fallback handling):', response.length);
                return response.map(poem => ({
                    ...poem,
                    isActive: poem.isActive !== undefined ? poem.isActive : true
                }));
            } else {
                console.error('PoemCache: API response has unexpected format:', response);
                return [];
            }
        } catch (error) {
            console.error('PoemCache: Failed to load poems from API:', error);

            // Return empty array instead of throwing to allow fallback poems
            return [];
        }
    }

    /**
     * Load cache from session storage
     */
    private loadFromSessionStorage(): void {
        try {
            const cached = sessionStorage.getItem(this.cacheKey);
            if (!cached) {
                console.log('PoemCache: No cached poems in session storage');
                return;
            }

            const poemCache: PoemCache = JSON.parse(cached);

            // Check cache version
            if (poemCache.version !== this.cacheVersion) {
                console.log('PoemCache: Cache version mismatch, clearing cache');
                this.clearSessionStorage();
                return;
            }

            // Check if cache is from current session (not expired)
            const now = Date.now();
            const cacheAge = now - poemCache.timestamp;
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours

            if (cacheAge > maxAge) {
                console.log('PoemCache: Cache expired, clearing cache');
                this.clearSessionStorage();
                return;
            }

            this.cache = poemCache.poems;
            console.log('PoemCache: Loaded poems from session storage:', this.cache.length);
        } catch (error) {
            console.error('PoemCache: Failed to load from session storage:', error);
            this.clearSessionStorage();
        }
    }

    /**
     * Save cache to session storage
     */
    private saveToSessionStorage(): void {
        if (!this.cache) return;

        try {
            const poemCache: PoemCache = {
                poems: this.cache,
                timestamp: Date.now(),
                version: this.cacheVersion
            };

            sessionStorage.setItem(this.cacheKey, JSON.stringify(poemCache));
            console.log('PoemCache: Saved poems to session storage:', this.cache.length);
        } catch (error) {
            console.error('PoemCache: Failed to save to session storage:', error);
        }
    }

    /**
     * Clear session storage cache
     */
    private clearSessionStorage(): void {
        try {
            sessionStorage.removeItem(this.cacheKey);
            console.log('PoemCache: Cleared session storage cache');
        } catch (error) {
            console.error('PoemCache: Failed to clear session storage:', error);
        }
    }
}

// Export singleton instance
export const poemCacheService = PoemCacheService.getInstance();